/**
 * The governance tabs of a policy: settings, control mappings, who has
 * acknowledged it, and what has happened to it.
 *
 * These read and write the governance layer, which is separate from the
 * document the policy was ingested from — see `schema/governance.ts`. A policy
 * with no governance row yet is not an error state; it is a document nobody
 * has governed, and the settings form is how that starts.
 *
 * @module components/regulation/policy/PolicyGovernanceTabs
 */
import { CheckIcon, Link2OffIcon, LinkIcon, PlusIcon, SearchIcon, UserIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { cn } from "~/lib/utils";
import { resolvePrimaryEnvironmentHttpUrl } from "../../../environments/primary/target";
import { useOrgProfile } from "../../../state/orgProfile";
import { usePolicies } from "../../../state/policies";
import {
  usePolicyAcknowledgments,
  usePolicyActivity,
  usePolicyComment,
  useGovernanceFrameworks,
  usePolicyGovernance,
  usePolicyMappings,
  useFrameworks,
  type PolicyLifecycleStatus,
  type PolicyReviewFrequency,
} from "../../../state/governance";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../../ui/dialog";
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxPopup,
  ComboboxValue,
} from "../../ui/combobox";
import { Input } from "../../ui/input";
import { InputGroup, InputGroupAddon, InputGroupInput } from "../../ui/input-group";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../../ui/select";
import { PolicyOwnersField } from "./PolicyOwnersField";

export const LIFECYCLE_LABELS: Record<PolicyLifecycleStatus, string> = {
  draft: "Draft",
  in_review: "In review",
  published: "Published",
  archived: "Archived",
};

export const FREQUENCY_LABELS: Record<PolicyReviewFrequency, string> = {
  none: "No review cycle",
  monthly: "Monthly",
  quarterly: "Quarterly",
  semiannual: "Every six months",
  yearly: "Yearly",
};

export const formatDate = (value: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? "—"
    : parsed.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
};

function Field({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-foreground">{label}</span>
      {children}
    </label>
  );
}

/**
 * Policy settings — the governance facts a reviewer asks about, as a card.
 *
 * Save is only live while the form differs from the server: a Save button
 * that always works invites clicking it "just in case", and a governance
 * activity trail should not fill with no-op saves.
 */
export function PolicySettingsTab({
  organizationId,
  policyId,
}: {
  readonly organizationId: string | null;
  readonly policyId: string;
}) {
  const { snapshot, isPending, save } = usePolicyGovernance(organizationId, policyId);
  const { canManage } = useOrgProfile(organizationId);
  const { snapshot: library, setJurisdictions: savePolicyJurisdictions } =
    usePolicies(organizationId);
  const policy = library?.policies.find((entry) => entry.id === policyId) ?? null;
  // Alphabetical: the org's adoption order is not a reading order.
  const orgJurisdictions = [...(library?.jurisdictions ?? [])].sort((left, right) =>
    left.name.localeCompare(right.name),
  );

  const { snapshot: frameworkList } = useGovernanceFrameworks(organizationId);
  const frameworks = frameworkList?.frameworks ?? [];

  const [title, setTitle] = useState("");
  const [frequency, setFrequency] = useState<PolicyReviewFrequency>("none");
  // "" is "none": the firm's own framework this document sits under.
  const [frameworkId, setFrameworkId] = useState<string>("");
  const [jurisdictions, setJurisdictionsDraft] = useState<ReadonlyArray<string>>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // The form follows the server until the user edits it; a policy another
  // admin changes while this page is open should not look stale here.
  useEffect(() => {
    setTitle(snapshot?.title ?? "");
    setFrequency(snapshot?.reviewFrequency ?? "none");
  }, [snapshot]);
  useEffect(() => {
    setJurisdictionsDraft(policy?.jurisdictionSlugs ?? []);
    setFrameworkId(policy?.governanceFrameworkId ?? "");
  }, [policy]);

  const ungoverned = snapshot === null && !isPending;
  const trimmedTitle = title.trim();
  const sameSet = (left: ReadonlyArray<string>, right: ReadonlyArray<string>) =>
    left.length === right.length && left.every((slug) => right.includes(slug));
  const frameworkDirty = frameworkId !== (policy?.governanceFrameworkId ?? "");
  const governanceDirty =
    ungoverned ||
    trimmedTitle !== (snapshot?.title ?? "") ||
    frequency !== (snapshot?.reviewFrequency ?? "none") ||
    frameworkDirty;
  const jurisdictionsDirty = !sameSet(jurisdictions, policy?.jurisdictionSlugs ?? []);
  const dirty = governanceDirty || jurisdictionsDirty;

  const toggleJurisdiction = (slug: string) =>
    setJurisdictionsDraft((current) =>
      current.includes(slug) ? current.filter((entry) => entry !== slug) : [...current, slug],
    );

  const onSave = async () => {
    setSaving(true);
    setMessage(null);
    // Two records, two writes: ownership and cadence live on governance, the
    // jurisdictions on the policy itself. Either failing reports its own error.
    const errors: Array<string> = [];
    if (governanceDirty) {
      const error = await save({
        title: trimmedTitle.length === 0 ? null : trimmedTitle,
        reviewFrequency: frequency,
        ...(frameworkDirty ? { frameworkId: frameworkId.length === 0 ? null : frameworkId } : {}),
      });
      if (error) errors.push(error);
    }
    if (jurisdictionsDirty) {
      const error = await savePolicyJurisdictions(policyId, jurisdictions);
      if (error) errors.push(error);
    }
    setSaving(false);
    setMessage(errors.length > 0 ? errors.join(" ") : "Saved.");
  };

  const onReset = () => {
    setTitle(snapshot?.title ?? "");
    setFrequency(snapshot?.reviewFrequency ?? "none");
    setFrameworkId(policy?.governanceFrameworkId ?? "");
    setJurisdictionsDraft(policy?.jurisdictionSlugs ?? []);
    setMessage(null);
  };

  const jurisdictionName = (slug: string) =>
    orgJurisdictions.find((jurisdiction) => jurisdiction.slug === slug)?.name ?? slug;


  return (
    // Its own container: the fields pair up when the card, not the window,
    // has room for two.
    <section className="@container/settings rounded-xl border border-border/60 bg-card p-5">
      <h2 className="text-sm font-semibold text-foreground">
        {ungoverned ? "Set up governance" : "Governance settings"}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        {ungoverned
          ? "Until this is saved, reviewers see no owner or review date for this policy."
          : "Who owns this policy, how often it is reviewed, and where it applies."}
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 @[30rem]/settings:grid-cols-2">
        {/* The document's real title. A filename with a date in it is not a
            title, and renaming here is what the rename recommendation does. */}
        <Field label="Title">
          <Input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={policy?.filename ?? "Name this policy"}
            disabled={!canManage}
          />
        </Field>

        {/* One way to assign, and it takes a set — see PolicyOwnersField. */}
        <PolicyOwnersField
          organizationId={organizationId}
          policyId={policyId}
          canManage={canManage}
        />

        <Field label="Framework">
          {/* The firm's own organising layer — "how have we organised ourselves
              to manage this?" — not the regime. A document under none is a
              gap the inspector points at. */}
          <Select
            value={frameworkId}
            onValueChange={(value) => setFrameworkId(value ?? "")}
            disabled={!canManage || frameworks.length === 0}
          >
            <SelectTrigger className="w-full">
              {/* One line: a long framework name truncates rather than
                  doubling the field's height beside its neighbour. */}
              <span className="min-w-0 flex-1 truncate text-start">
                {frameworks.find((framework) => framework.id === frameworkId)?.name ??
                  (frameworks.length === 0 ? "No frameworks yet — organise them on the Monitor" : "None")}
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">None</SelectItem>
              {frameworks.map((framework) => (
                <SelectItem key={framework.id} value={framework.id}>
                  {framework.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <div className="flex flex-col gap-1.5">
          <Field label="Review cadence">
            <Select
              value={frequency}
              onValueChange={(value) => setFrequency(value as PolicyReviewFrequency)}
              disabled={!canManage}
            >
              <SelectTrigger className="w-full">
                <span className="min-w-0 flex-1 truncate text-start">{FREQUENCY_LABELS[frequency]}</span>
              </SelectTrigger>
              <SelectContent>
                {Object.entries(FREQUENCY_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {/* The next review date is derived from the cadence, so it lives
              under it as a consequence rather than posing as a fourth field. */}
          {snapshot?.reviewDueAt ? (
            <span className="text-xs text-muted-foreground">
              Next review {formatDate(snapshot.reviewDueAt)}.
            </span>
          ) : frequency !== "none" ? (
            <span className="text-xs text-muted-foreground">
              The next review is scheduled from this when you save.
            </span>
          ) : null}
        </div>

        <div className="@[30rem]/settings:col-span-2">
          <Field label="Jurisdictions">
            {orgJurisdictions.length === 0 ? (
              <span className="text-sm text-muted-foreground">
                This organization has no jurisdictions yet.
              </span>
            ) : (
              // A real multi-select field: chosen jurisdictions sit inside the
              // input as removable tokens and the list offers the rest, the
              // same shape as any tag field -- not a loose row of pills.
              <Combobox
                multiple
                items={orgJurisdictions.map((jurisdiction) => jurisdiction.slug)}
                value={[...jurisdictions]}
                onValueChange={(next) => setJurisdictionsDraft(next)}
                itemToStringLabel={(slug) => jurisdictionName(slug)}
                disabled={!canManage}
              >
                <ComboboxChips>
                  <ComboboxValue>
                    {(selected: ReadonlyArray<string>) =>
                      selected.map((slug) => (
                        <ComboboxChip key={slug} aria-label={jurisdictionName(slug)}>
                          {jurisdictionName(slug)}
                        </ComboboxChip>
                      ))
                    }
                  </ComboboxValue>
                  <ComboboxChipsInput
                    placeholder={jurisdictions.length === 0 ? "Add a jurisdiction" : undefined}
                  />
                </ComboboxChips>
                <ComboboxPopup>
                  <ComboboxEmpty>No matching jurisdiction.</ComboboxEmpty>
                  <ComboboxList>
                    {(slug: string) => (
                      <ComboboxItem key={slug} value={slug}>
                        {jurisdictionName(slug)}
                      </ComboboxItem>
                    )}
                  </ComboboxList>
                </ComboboxPopup>
              </Combobox>
            )}
          </Field>
          {policy?.jurisdictionSource === "detected" && !jurisdictionsDirty ? (
            <span className="mt-1 block text-xs text-muted-foreground">
              Detected from the document when it was processed; change it here if that is wrong.
            </span>
          ) : null}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-border/60 pt-4">
        <Button
          type="button"
          size="sm"
          onClick={() => void onSave()}
          disabled={!canManage || saving || !dirty}
          className="cursor-pointer"
        >
          {saving ? "Saving…" : ungoverned ? "Save and start governing" : "Save changes"}
        </Button>
        {dirty && !ungoverned && !saving && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onReset}
            className="cursor-pointer"
          >
            Discard
          </Button>
        )}
        {message !== null && !dirty && (
          <span className="text-xs text-muted-foreground">{message}</span>
        )}
        {message !== null && dirty && message !== "Saved." && (
          <span className="text-xs text-destructive">{message}</span>
        )}
        {!canManage && (
          <span className="text-xs text-muted-foreground">
            Only organization admins can change these settings.
          </span>
        )}
      </div>
    </section>
  );
}

/** Name, regime, unlink — the regime column only on a wide page. */
const MAPPING_ROW =
  "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 @[36rem]/policy:grid-cols-[minmax(0,1fr)_auto_auto]";

/**
 * Controls relevant to this policy — the Mappings tab. Linking a control is
 * what makes regime progress count this policy; unlinking is the audit
 * answer "this policy no longer covers that".
 */
export function PolicyMappingsTab({
  organizationId,
  policyId,
}: {
  readonly organizationId: string | null;
  readonly policyId: string;
}) {
  const { snapshot, isPending, refresh } = usePolicyMappings(organizationId, policyId);
  const { snapshot: frameworkList } = useFrameworks(organizationId);
  const { canManage } = useOrgProfile(organizationId);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const mappings = snapshot?.mappings ?? [];
  const adopted = (frameworkList?.frameworks ?? []).filter((f) => f.enabled);
  const mappedIds = useMemo(
    () => new Set(mappings.map((mapping) => mapping.controlId)),
    [mappings],
  );

  const mutateMapping = async (controlId: string, method: "POST" | "DELETE") => {
    if (!organizationId) return false;
    setBusy(true);
    setMessage(null);
    let ok = false;
    try {
      const params =
        method === "DELETE"
          ? { organizationId, policyId, controlId }
          : { organizationId, policyId };
      const response = await fetch(
        resolvePrimaryEnvironmentHttpUrl("/api/amoreg/regulation/mappings", params),
        {
          method,
          credentials: "include",
          headers: { "content-type": "application/json" },
          ...(method === "POST" ? { body: JSON.stringify({ controlId }) } : {}),
        },
      );
      if (!response.ok) {
        setMessage(
          response.status === 403
            ? "Only organization admins can change mappings."
            : "The mapping could not be updated.",
        );
      } else {
        ok = true;
        refresh();
      }
    } catch {
      setMessage("The mapping could not be updated.");
    }
    setBusy(false);
    return ok;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-foreground">Controls</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Controls relevant to this policy.
          </p>
        </div>
        {/* The button exists only when there is something to link to: an
            enabled regime, and the right to change mappings. */}
        {canManage && adopted.length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPickerOpen(true)}
            className="cursor-pointer gap-1"
          >
            <PlusIcon className="size-3.5" />
            Link control
          </Button>
        )}
      </div>

      {message !== null && <p className="text-xs text-destructive">{message}</p>}

      {/* Mounted only while open, so every opening starts clean: no remembered
          framework, search or scroll position from last time. */}
      {pickerOpen && (
        <LinkControlDialog
          organizationId={organizationId}
          frameworks={adopted}
          mappedIds={mappedIds}
          busy={busy}
          onLink={(controlId) => mutateMapping(controlId, "POST")}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {mappings.length === 0 ? (
        isPending && snapshot === null ? null : (
          <div className="rounded-xl border border-dashed border-border/60 px-6 py-12 text-center">
            <LinkIcon className="mx-auto size-5 text-muted-foreground/60" />
            <p className="mt-3 text-sm font-medium text-foreground">No controls linked</p>
            <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
              Link this policy to the controls it answers, and regime progress
              starts counting it.
            </p>
          </div>
        )
      ) : (
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
          {/* The regime is a column on a wide page and a second line under
              the control on a narrow one: a long regime name in an auto
              column squeezed the control itself out of sight. */}
          <div className={cn(MAPPING_ROW, "border-b border-border/60 bg-muted/30 px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground")}>
            <span>Name</span>
            <span className="hidden @[36rem]/policy:block">Regime</span>
            <span className="w-8" />
          </div>
          {mappings.map((mapping) => (
            <div
              key={mapping.controlId}
              className={cn(MAPPING_ROW, "border-b border-border/60 px-4 py-3 last:border-b-0")}
            >
              <div className="min-w-0">
                <span className="block truncate text-sm text-foreground" title={`${mapping.code} ${mapping.title}`}>
                  <span className="me-2 rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                    {mapping.code}
                  </span>
                  {mapping.title}
                  {mapping.suggestedByAgent && (
                    <span className="ms-2 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] text-primary">
                      Suggested by Inspector
                    </span>
                  )}
                </span>
                <span className="mt-1 block truncate text-xs text-muted-foreground @[36rem]/policy:hidden">
                  {mapping.frameworkName}
                </span>
              </div>
              <span
                className="hidden max-w-[18rem] truncate rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground @[36rem]/policy:block"
                title={mapping.frameworkName}
              >
                {mapping.frameworkName}
              </span>
              {canManage ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void mutateMapping(mapping.controlId, "DELETE")}
                  aria-label={`Unlink ${mapping.code}`}
                  className="flex size-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                >
                  <Link2OffIcon className="size-4" />
                </button>
              ) : (
                <span className="w-8" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

type PickableControl = { id: string; code: string; title: string; category?: string | null };

/**
 * Pick the controls this policy answers: a regime down the left, its
 * controls on the right, a search box over them, and a Link button per row
 * that becomes a check once it is done. Nothing here outlives the dialog.
 */
function LinkControlDialog({
  organizationId,
  frameworks,
  mappedIds,
  busy,
  onLink,
  onClose,
}: {
  readonly organizationId: string | null;
  readonly frameworks: ReadonlyArray<{ id: string; name: string }>;
  readonly mappedIds: ReadonlySet<string>;
  readonly busy: boolean;
  readonly onLink: (controlId: string) => Promise<boolean>;
  readonly onClose: () => void;
}) {
  const [frameworkId, setFrameworkId] = useState<string>(frameworks[0]?.id ?? "");
  const [controls, setControls] = useState<ReadonlyArray<PickableControl> | null>(null);
  const [query, setQuery] = useState("");
  // Linked in this sitting: a row turns into a check instead of vanishing
  // from under the pointer when the mapping list refreshes.
  const [linked, setLinked] = useState<ReadonlySet<string>>(() => new Set());
  const [linking, setLinking] = useState<string | null>(null);

  useEffect(() => {
    if (!organizationId || frameworkId.length === 0) return;
    let cancelled = false;
    setControls(null);
    void (async () => {
      try {
        const response = await fetch(
          resolvePrimaryEnvironmentHttpUrl("/api/amoreg/regulation/controls", {
            organizationId,
            frameworkId,
          }),
          { credentials: "include" },
        );
        const json = response.ok
          ? ((await response.json()) as { controls?: PickableControl[] })
          : { controls: [] };
        if (!cancelled) setControls(json.controls ?? []);
      } catch {
        if (!cancelled) setControls([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId, frameworkId]);

  const needle = query.trim().toLowerCase();
  const visible = (controls ?? []).filter(
    (control) =>
      needle.length === 0 ||
      control.code.toLowerCase().includes(needle) ||
      control.title.toLowerCase().includes(needle) ||
      (control.category ?? "").toLowerCase().includes(needle),
  );
  const alreadyLinked = (controls ?? []).filter((control) => mappedIds.has(control.id)).length;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogPopup className="flex max-h-[80vh] max-w-3xl flex-col">
        <DialogHeader>
          <DialogTitle>Link controls</DialogTitle>
          <DialogDescription>
            Pick the requirements this policy answers. Regime progress counts a
            control once a policy is linked to it.
          </DialogDescription>
        </DialogHeader>
        <DialogPanel className="min-h-0 flex-1 p-0">
          <div className="grid h-full min-h-0 grid-cols-[13rem_1fr]">
            <nav
              aria-label="Regimes"
              className="min-h-0 overflow-y-auto border-r border-border/60 py-2"
            >
              {frameworks.map((framework) => {
                const active = framework.id === frameworkId;
                return (
                  <button
                    key={framework.id}
                    type="button"
                    onClick={() => {
                      setFrameworkId(framework.id);
                      setQuery("");
                    }}
                    className={cn(
                      "flex w-full cursor-pointer items-center px-4 py-2 text-left text-sm transition-colors",
                      active
                        ? "bg-muted font-medium text-foreground"
                        : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                    )}
                  >
                    <span className="truncate">{framework.name}</span>
                  </button>
                );
              })}
            </nav>
            <div className="flex min-h-0 flex-col">
              <div className="border-b border-border/60 px-4 py-3">
                <div className="relative">
                  <InputGroup className="h-8">
                    <InputGroupAddon className="text-muted-foreground">
                      <SearchIcon className="size-3.5" />
                    </InputGroupAddon>
                    <InputGroupInput
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      placeholder="Search by code, title or category"
                      autoFocus
                    />
                  </InputGroup>
                </div>
                {controls !== null && controls.length > 0 && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {controls.length} controls · {alreadyLinked + linked.size} linked to this policy
                  </p>
                )}
              </div>
              <ul className="min-h-0 flex-1 overflow-y-auto">
                {controls === null ? (
                  <li className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</li>
                ) : visible.length === 0 ? (
                  <li className="px-4 py-8 text-center text-sm text-muted-foreground">
                    {controls.length === 0 ? "This regime has no controls." : "No controls match."}
                  </li>
                ) : (
                  visible.map((control) => {
                    const done = mappedIds.has(control.id) || linked.has(control.id);
                    return (
                      <li
                        key={control.id}
                        className="flex items-center gap-3 border-b border-border/40 px-4 py-2.5 last:border-b-0"
                      >
                        <span className="w-16 shrink-0 rounded bg-muted px-1.5 py-0.5 text-center font-mono text-[11px] text-muted-foreground">
                          {control.code}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-foreground">{control.title}</span>
                          {control.category ? (
                            <span className="block text-xs text-muted-foreground">{control.category}</span>
                          ) : null}
                        </span>
                        {done ? (
                          <span className="flex w-20 items-center justify-center gap-1 text-xs text-primary">
                            <CheckIcon className="size-3.5" />
                            Linked
                          </span>
                        ) : (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={busy}
                            className="w-20 cursor-pointer"
                            onClick={() => {
                              setLinking(control.id);
                              void onLink(control.id).then((ok) => {
                                setLinking(null);
                                if (ok) setLinked((prev) => new Set(prev).add(control.id));
                              });
                            }}
                          >
                            {linking === control.id ? "Linking…" : "Link"}
                          </Button>
                        )}
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          </div>
        </DialogPanel>
        <DialogFooter>
          <DialogClose render={<Button size="sm" className="cursor-pointer">Done</Button>} />
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

/** Who has confirmed they read the published version. */
export function PolicyAcknowledgmentsTab({
  organizationId,
  policyId,
}: {
  readonly organizationId: string | null;
  readonly policyId: string;
}) {
  const { snapshot, isPending } = usePolicyAcknowledgments(organizationId, policyId);
  const acknowledgments = snapshot?.acknowledgments ?? [];

  if (acknowledgments.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border/60 px-6 py-12 text-center">
        <UserIcon className="mx-auto size-5 text-muted-foreground/60" />
        <p className="mt-3 text-sm font-medium text-foreground">
          {isPending ? "Loading acknowledgments…" : "Nobody has acknowledged this yet"}
        </p>
        <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
          Acknowledgments are pinned to the published version, so republishing asks
          everyone again.
        </p>
      </div>
    );
  }

  return (
    <ul className="divide-y divide-border/60 overflow-hidden rounded-xl border border-border/60 bg-card">
      {acknowledgments.map((entry) => (
        <li key={`${entry.userId}:${entry.policyVersion}`} className="flex items-center gap-3 px-4 py-3">
          <CheckIcon className="size-4 shrink-0 text-primary" />
          <span className="min-w-0 flex-1 truncate text-sm text-foreground">
            {entry.email ?? entry.userId}
          </span>
          <span className="shrink-0 text-xs text-muted-foreground">
            v{entry.policyVersion} · {formatDate(entry.acknowledgedAt)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** "3 days ago" for the activity feed; exact time on hover via title. */
function relativeTime(value: string): string {
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return "";
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  const steps: ReadonlyArray<readonly [number, number, Intl.RelativeTimeFormatUnit]> = [
    [60, 1, "second"],
    [3600, 60, "minute"],
    [86400, 3600, "hour"],
    [2592000, 86400, "day"],
    [31536000, 2592000, "month"],
    [Number.POSITIVE_INFINITY, 31536000, "year"],
  ];
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  for (const [limit, divisor, unit] of steps) {
    if (seconds < limit) return formatter.format(-Math.round(seconds / divisor), unit);
  }
  return "";
}

const ACTIVITY_VERBS: Record<string, string> = {
  created: "created the policy",
  published: "published the policy",
  archived: "archived the policy",
  reviewed: "reviewed the policy",
  owner_changed: "changed the owner",
  review_changed: "changed the review cycle",
  mapping_changed: "changed control mappings",
  file_replaced: "uploaded a new version",
  finding_attached: "attached a finding",
  finding_accepted: "accepted a finding (previous workflow)",
  finding_closed: "closed a finding",
  finding_reopened: "reopened a finding",
  finding_resolved: "resolved a finding",
  finding_dismissed: "closed a finding",
  finding_revised: "revised a finding",
  finding_withdrawn: "withdrew a finding",
  recommendation_raised: "raised a suggestion",
  recommendation_accepted: "accepted a suggestion",
  recommendation_applied: "applied a suggestion",
  recommendation_dismissed: "dismissed a suggestion",
  recommendation_reopened: "reopened a suggestion",
  recommendation_revised: "revised a suggestion",
  recommendation_withdrawn: "withdrew a suggestion",
  comment: "commented",
};

function ActorAvatar({ label }: { readonly label: string }) {
  return (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">
      {label.slice(0, 1).toUpperCase()}
    </span>
  );
}

/** What has happened to this policy. */
export function PolicyActivityTab({
  organizationId,
  policyId,
}: {
  readonly organizationId: string | null;
  readonly policyId: string;
}) {
  const { snapshot, isPending } = usePolicyActivity(organizationId, policyId);
  const activity = (snapshot?.activity ?? []).filter((entry) => entry.kind !== "comment");

  if (activity.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border/60 px-6 py-12 text-center">
        <p className="text-sm font-medium text-foreground">
          {isPending ? "Loading activity…" : "No activity recorded yet"}
        </p>
        <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
          Publishing, review and ownership changes are recorded here.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-base font-semibold text-foreground">Recent activity</h2>
      <ul className="mt-3 divide-y divide-border/60">
        {activity.map((entry) => {
          const actor = entry.actorEmail ?? "Inspector";
          return (
            <li key={entry.id} className="flex items-center gap-3 py-3">
              <ActorAvatar label={actor} />
              {/* Two lines on a narrow page, so the verb is not cut off
                  right after a long email address. */}
              <p className="min-w-0 flex-1 text-sm line-clamp-2 [overflow-wrap:anywhere] @[36rem]/policy:line-clamp-1">
                <span className="font-medium text-foreground">{actor}</span>{" "}
                <span className="text-muted-foreground">
                  {ACTIVITY_VERBS[entry.kind] ?? entry.kind.replace(/_/g, " ")}
                  {entry.detail && entry.kind !== "comment" ? ` — ${entry.detail}` : ""}
                </span>
              </p>
              <span
                className="shrink-0 text-xs text-muted-foreground"
                title={new Date(entry.createdAt).toLocaleString()}
              >
                {relativeTime(entry.createdAt)}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Comments: collaboration on a policy, kept apart from the audit trail the
 * Activity tab shows — a question is not a governance event.
 */
export function PolicyCommentsTab({
  organizationId,
  policyId,
}: {
  readonly organizationId: string | null;
  readonly policyId: string;
}) {
  const { snapshot, isPending, refresh } = usePolicyActivity(organizationId, policyId);
  const postComment = usePolicyComment(organizationId, policyId, refresh);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const comments = (snapshot?.activity ?? []).filter((entry) => entry.kind === "comment");

  const onPost = async () => {
    const text = draft.trim();
    if (text.length === 0) return;
    setPosting(true);
    setMessage(null);
    const error = await postComment(text);
    setPosting(false);
    setMessage(error);
    if (error === null) setDraft("");
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border/60 bg-card p-3">
        <textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Leave a comment"
          rows={3}
          className="w-full resize-y rounded-md border-0 bg-transparent p-1 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none"
        />
        <div className="flex items-center justify-end gap-3 border-t border-border/60 pt-2">
          {message !== null && <p className="text-xs text-destructive">{message}</p>}
          <Button
            type="button"
            size="sm"
            onClick={() => void onPost()}
            disabled={posting || draft.trim().length === 0}
            className="cursor-pointer"
          >
            {posting ? "Posting…" : "Comment"}
          </Button>
        </div>
      </div>

      {comments.length === 0 ? (
        <p className="px-1 text-sm text-muted-foreground">
          {isPending ? "Loading comments…" : "No comments yet. Start the conversation."}
        </p>
      ) : (
        <ul className="space-y-3">
          {comments.map((entry) => {
            const actor = entry.actorEmail ?? "Inspector";
            return (
              <li key={entry.id} className="rounded-xl border border-border/60 bg-card p-4">
                <div className="flex items-center gap-2.5">
                  <ActorAvatar label={actor} />
                  <span className="text-sm font-medium text-foreground">{actor}</span>
                  <span
                    className="text-xs text-muted-foreground"
                    title={new Date(entry.createdAt).toLocaleString()}
                  >
                    {relativeTime(entry.createdAt)}
                  </span>
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">
                  {entry.detail}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
